import { FormGroup, FormBuilder, AbstractControl } from "@angular/forms";
import { FieldFactory, ParentFieldFactory, CreateFormContextOptions } from "../field-factory";
import { FormContext } from "../../form/form-context";
import { FormRootDirective } from "../../form/form-root.directive";
import { DynamicGroupController } from "./dynamic-group-controller";
import { GroupFormFieldSetting } from "./group-form-field-setting";
import { FormFieldGroupComponent } from "./form-field-group-component";
import { ViewRef } from "@angular/core";
import { FormFieldSetting } from "../../form-settings/form-field-setting";
import { FormFieldSettings } from "../../form-settings/form-field-settings";

export class GroupFieldFactory implements FieldFactory<FormGroup, GroupFormFieldSetting<any>> {
  constructor(private parentFieldFactory: ParentFieldFactory, private formBuilder: FormBuilder) {}

  public create<M>(
    options: CreateFormContextOptions<M, FormGroup, GroupFormFieldSetting<M>>
  ): FormContext<M, FormGroup> {
    const { setting, settings } = options;
    const [formControl, children] = this.createChildContexts(options);

    const dynamicController = new DynamicGroupController(formControl, this.parentFieldFactory, setting, settings);

    const groupFormContext: FormContext<M, FormGroup> = {
      ...this.parentFieldFactory.resolveComponent<M, FormGroup>(
        formControl,
        setting,
        options.initialValue,
        dynamicController
      ),
      children
    };

    const groupComponent = groupFormContext.componentRef.instance as FormFieldGroupComponent;

    if (!groupComponent.formRoot) {
      throw new Error(
        `Group component ${setting.name} is not a group component. It is missing the 'formRoot' directive.`
      );
    }

    dynamicController.onInit(groupFormContext);

    return groupFormContext;
  }

  public render(formRoot: FormRootDirective, root: FormContext<any, FormGroup>): ViewRef {
    const viewRef = formRoot.viewContainerRef.insert(root.componentRef.hostView);
    viewRef.detectChanges();

    const entries = Object.entries(root.children);
    entries.forEach(([_id, context]) => {
      const component = root.componentRef.instance as FormFieldGroupComponent;
      this.parentFieldFactory.render(component.formRoot, context);
    });

    return viewRef;
  }

  updateChildren(context: FormContext<any, FormGroup>): void {
    const controlEntires = Object.entries(context.formControl.controls);

    const entries = Object.entries(context.children);
    entries.forEach(([_id, child]) => {
      const missingFormControl = controlEntires.find(entry => child.formControl === entry[1]);
      if (missingFormControl) {
        context.formControl.setControl(missingFormControl[0], missingFormControl[1]);
      }
    });
  }

  private createChildContexts<M>(
    options: CreateFormContextOptions<M, FormGroup, GroupFormFieldSetting<M>>
  ): [FormGroup, { [id: string]: FormContext<M> }] {
    const { setting, settings } = options;

    if (options.formControl) {
      const controlEntires = Object.entries(options.formControl.controls);
      const children: { [id: string]: FormContext<M, AbstractControl> } = {};

      controlEntires.forEach(([id, formControl]) => {
        children[id] = this.parentFieldFactory.create<any>({
          formControl,
          setting: this.getSetting(id, settings),
          settings
        });
      });

      return [options.formControl, children];
    }

    const { initialValue: value } = options;

    if (typeof value !== "object") {
      throw new Error(`Value of group ${setting.name} is not an object`);
    }

    const children: { [id: string]: FormContext<M, AbstractControl> } = {};
    const formChildren: { [id: string]: AbstractControl } = {};
    Object.entries(value).forEach(([id, child]) => {
      children[id] = this.parentFieldFactory.create<M>({
        initialValue: child,
        setting: this.getSetting(id, settings),
        settings
      });
      formChildren[id] = children[id].formControl;
    });

    const formControl = this.formBuilder.group(formChildren);

    return [formControl, children];
  }

  private getSetting<M>(id: string, settings: FormFieldSettings): FormFieldSetting<M> {
    const childSetting = settings.get(id) as FormFieldSetting<M>;
    if (!childSetting) {
      throw new Error(`Form field '${id}' has no assigned form field setting.`);
    }

    return childSetting;
  }
}
